import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    GET_AEPS_DEVICE_CONFIG,
    FING_PAY_2FA_BIOMETRIC_REQUEST,
    FING_PAY_BANK_LIST,
    FING_PAY_CHECK_EKYC_STATUS,
    POST_AEPS_LOGIN_STATUS,
} from './../../Utils/appConstants';
import { useHistory } from "react-router-dom";
import { getGeoLocation } from '../../Utils/function';
import xmlJs from 'xml-js';
import SettelTOBank from './../moveToBank/index';
import BankSetting from './../banksetting';
import { postRequest, getRequest } from '../../Utils/AppApi';
import CashWithdrawal from './CashWithdrawal';
import BioDevice from './../aepsInstant/bioDevice';
import BioDeviceList from './../aepsInstant/bioDeviceList';
import BalanceEnquiry from './BalanceEnquiry';
import MiniStatement from './MiniStatement';
import AePSHistory from './../aepsInstant/AePSHistory';
import AePSRegister from './AePSRegister';
import { toast } from 'react-toastify';
import { Tabs, Modal, Checkbox } from 'antd';
import { reactLocalStorage } from 'reactjs-localstorage';
import moment from "moment";

// ====================== RD SERVICE - PORT ONLY DISCOVERY (scheme locked to DB) ======================
const PORTS = Array.from({ length: 21 }, (_, i) => 11100 + i); // 11100-11120
const RD_CACHE_KEY = 'rd_service_port_v1';
const RD_CACHE_TTL = 15 * 60 * 1000; // 15 minutes
const RD_TIMEOUT = 800;

const getCachedPort = () => {
    try {
        const raw = localStorage.getItem(RD_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.ts > RD_CACHE_TTL) return null;
        return parsed.port;
    } catch {
        return null;
    }
};

const setCachedPort = (port) => {
    localStorage.setItem(RD_CACHE_KEY, JSON.stringify({ port, ts: Date.now() }));
};

const clearRDCache = () => {
    localStorage.removeItem(RD_CACHE_KEY);
};

const replacePort = (url, newPort) => {
    if (!url || !newPort) return url;
    try {
        return url.replace(/:(\d+)(?=\/|$)/, `:${newPort}`);
    } catch {
        return url;
    }
};

/** Extract scheme from DB URL → "http" or "https" */
const getSchemeFromUrl = (url) => {
    if (!url) return 'http';
    const m = String(url).match(/^(https?):\/\//i);
    return m ? m[1].toLowerCase() : 'http';
};

const probePort = async (scheme, port) => {
    try {
        const res = await axios({
            method: 'RDSERVICE',
            url: `${scheme}://127.0.0.1:${port}/`,
            headers: { 'Content-Type': 'text/xml; charset=UTF-8' },
            data: ' ',
            timeout: RD_TIMEOUT,
            validateStatus: () => true,
        });

        if (res.status < 200 || res.status >= 400) return false;
        const xml = typeof res.data === 'string' ? res.data : '';
        return xml.toLowerCase().includes('rdservice');
    } catch {
        return false;
    }
};

/**
 * Discover working port using ONLY the scheme from DB URL
 * Never switches http ↔ https
 *
 * force=false → try cache first; if dead, full scan
 * force=true  → ignore cache, scan all ports 11100-11120
 */
const discoverPort = async (force = false, preferredScheme = 'http') => {
    const scheme = preferredScheme === 'https' ? 'https' : 'http';

    // 1. Try cache with same scheme only
    if (!force) {
        const cached = getCachedPort();
        if (cached) {
            const alive = await probePort(scheme, cached);
            if (alive) return cached;
            clearRDCache();
        }
    } else {
        clearRDCache();
    }

    // 2. Full scan — same scheme only (no http↔https flip)
    for (const port of PORTS) {
        const alive = await probePort(scheme, port);
        if (alive) {
            setCachedPort(port);
            return port;
        }
    }

    return null;
};
// ====================== END RD SERVICE ======================

const { TabPane } = Tabs;

const MyComponent = () => {
    const history = useHistory();
    const [isConsent, setConsent] = useState(false);
    const tabPosition = 'left';
    const [fingerprint, setFingerprint] = useState(null);
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");
    const [getBankList, setBankList] = useState([]);
    const [deviceConfig, setDeviceConfig] = useState({});
    const [isAuthAePSModel, setAuthAePSModel] = useState(false);
    const [isErrorAuth, setErrorAuth] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [subTabs, setSubTabs] = useState('1');
    const [getDataField, setDataField] = useState([]);
    const [modal, contextHolder] = Modal.useModal();
    const [isDeviceConnected, setIsDeviceConnected] = useState(false);

    // RD Service states
    const [activePort, setActivePort] = useState(null);
    const [isDiscovering, setIsDiscovering] = useState(false);

    // Capture lock — prevents frequent info/capture calls
    const isCapturingRef = useRef(false);

    // Keep original DB URLs (never overwrite permanently)
    const originalConfigRef = useRef({ infoUrl: null, captureUrl: null, xmlData: null });

    // ---------- Get final URLs (original DB URL + discovered port) ----------
    const getInfoUrl = (port = activePort) => {
        const base = originalConfigRef.current.infoUrl || deviceConfig?.infoUrl;
        if (!base) return null;
        return port ? replacePort(base, port) : base;
    };

    const getCaptureUrl = (port = activePort) => {
        const base = originalConfigRef.current.captureUrl || deviceConfig?.captureUrl;
        if (!base) return null;
        return port ? replacePort(base, port) : base;
    };

    // ---------- Discover port ----------
    const discoverDevice = async (force = false) => {
        setIsDiscovering(true);
        try {
            // Save originals once
            if (deviceConfig?.infoUrl && !originalConfigRef.current.infoUrl) {
                originalConfigRef.current = {
                    infoUrl: deviceConfig.infoUrl,
                    captureUrl: deviceConfig.captureUrl,
                    xmlData: deviceConfig.xmlData,
                };
            }

            // Scheme locked to DB URL — never flips
            const baseInfoUrl = originalConfigRef.current.infoUrl || deviceConfig?.infoUrl;
            const scheme = getSchemeFromUrl(baseInfoUrl);

            const port = await discoverPort(force, scheme);
            if (port) {
                setActivePort(port);

                const finalInfoUrl = getInfoUrl(port);
                const finalCaptureUrl = getCaptureUrl(port);

                // Update deviceConfig for child components
                setDeviceConfig((prev) => ({
                    ...prev,
                    infoUrl: finalInfoUrl || prev.infoUrl,
                    captureUrl: finalCaptureUrl || prev.captureUrl,
                }));

                const connected = await checkDeviceConnected(finalInfoUrl);
                setIsDeviceConnected(connected);
                return port;
            } else {
                setActivePort(null);
                setIsDeviceConnected(false);
                return null;
            }
        } catch (err) {
            console.error('Discovery error:', err);
            setActivePort(null);
            setIsDeviceConnected(false);
            return null;
        } finally {
            setIsDiscovering(false);
        }
    };

    // ---------- Check device connected ----------
    const checkDeviceConnected = async (infoUrl) => {
        if (!infoUrl) return false;
        try {
            const res = await axios({
                method: 'DEVICEINFO',
                url: infoUrl,
                headers: { 'Content-Type': 'text/xml; charset=UTF-8' },
                data: ' ',
                timeout: 3000,
                validateStatus: () => true,
            });

            if (res.status < 200 || res.status >= 400) return false;

            const jsonData = xmlJs.xml2json(res.data, { compact: true });
            const parsed = JSON.parse(jsonData);

            if (!parsed?.DeviceInfo) return false;

            const params = parsed?.DeviceInfo?.additional_info?.Param;

            let srnoValue = null;
            if (Array.isArray(params)) {
                const srnoParam = params.find((p) => p._attributes?.name === 'srno');
                srnoValue = srnoParam?._attributes?.value;
            } else if (params?._attributes?.name === 'srno') {
                srnoValue = params._attributes?.value;
            }

            return !!srnoValue && srnoValue.trim() !== '';
        } catch {
            return false;
        }
    };

    const countDown = () => {
        let secondsToGo = 10;
        const instance = modal.success({
            title: <><h3>Your aeps service service activation is under process. please wait a same time.</h3></>,
            width: "600px",
            okText: "",
        });
        const timer = setInterval(() => {
            secondsToGo -= 1;
        }, 1000);
        setTimeout(() => {
            clearInterval(timer);
            instance.destroy();
            history.push({ pathname: "/dashboard" });
        }, secondsToGo * 1000);
    };

    const fetchLocation = async () => {
        let data = await getGeoLocation();
        setLatitude(data.latitude);
        setLongitude(data.longitude);
    };

    useEffect(() => {
        ekyChackApproved();
        aepsLoginStatus();
        getBankListData();
        aepsConfig();
        fetchLocation();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Discover port after deviceConfig is loaded from DB
    useEffect(() => {
        if (deviceConfig?.infoUrl && !originalConfigRef.current.infoUrl) {
            originalConfigRef.current = {
                infoUrl: deviceConfig.infoUrl,
                captureUrl: deviceConfig.captureUrl,
                xmlData: deviceConfig.xmlData,
            };
            discoverDevice(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceConfig?.infoUrl]);

    useEffect(() => {
        if (fingerprint) {
            aepsLogin(fingerprint);
        }
    }, [fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

    const existInputData = (event, select) => {
        if (select) {
            setDataField({ ...getDataField, "bankiin": select });
        } else {
            const { name, value } = event.target ? event.target : event;
            setDataField({ ...getDataField, [name]: value });
        }
    };

    const clearExistInputData = () => {
        window.location.reload();
        setDataField({});
    };

    const aepsConfig = () => {
        getRequest(GET_AEPS_DEVICE_CONFIG, history).then((response) => {
            if (response.success === true) {
                setDeviceConfig(response.data);
                reactLocalStorage.set("aepsConfig", JSON.stringify(response.data));
            } else {
                setAuthAePSModel(true);
                setErrorAuth(false);
                setErrorMessage(response.message);
            }
        });
    };

    const ekyChackApproved = () => {
        getRequest(FING_PAY_CHECK_EKYC_STATUS, history).then((response) => {
            if (response.success === true) {
                if (!response.data.isCompleted) {
                    countDown();
                }
            } else {
                countDown();
            }
        });
    };

    const getBankListData = async () => {
        await getRequest(FING_PAY_BANK_LIST, history).then((response) => {
            if (response.success === true) {
                setBankList(response.data.map(item => {
                    return { "value": item.iinno, "label": item.bankName };
                }));
            } else {
                toast.error(response.message);
            }
        }).catch(function (error) {
            console.log(error);
        });
    };

    const aepsLogin = (data) => {
        const payload = {
            devicetype: "W",
            latitude: latitude,
            longitude: longitude,
            isXml: true,
            biometricData: data,
            serviceType: "AEPS",
        };

        postRequest(FING_PAY_2FA_BIOMETRIC_REQUEST, payload, history).then((response) => {
            if (response.success === true) {
                setAuthAePSModel(false);
            } else {
                if (response.rekyc) {
                    history.push({ pathname: "/partner-ekyc" });
                }
                setAuthAePSModel(true);
                toast.error(response.message);
            }
        });
    };

    const aepsLoginStatus = () => {
        let payload = {
            date: moment().format('YYYY-MM-DD'),
            key: "AEPS_FINGPAY",
            serviceType: "AEPS",
        };

        postRequest(POST_AEPS_LOGIN_STATUS, payload, history).then((response) => {
            if (response.success === true) {
                setAuthAePSModel(false);
            } else {
                setAuthAePSModel(true);
            }
        });
    };

    // ---------- Fingerprint Capture ----------
    const scanFingerPrintData = async () => {
        // Hard lock — prevents frequent info/capture calls
        if (isCapturingRef.current) {
            console.log("Capture already running — skipped");
            return;
        }
        isCapturingRef.current = true;

        try {
            // 1. Ensure port
            let port = activePort;
            if (!port) {
                port = await discoverDevice(true);
                if (!port) {
                    toast.error("No RD Service found. Please install/start RD Service for your biometric device.");
                    setConsent(false);
                    return;
                }
            }

            // 2. DEVICEINFO — only once
            let infoUrl = getInfoUrl(port);
            let status = await checkDeviceConnected(infoUrl);
            setIsDeviceConnected(status);

            // If current port fails → force rediscover (same scheme)
            if (!status) {
                console.log(`Port ${port} failed / no device. Scanning all ports...`);
                clearRDCache();
                setActivePort(null);

                port = await discoverDevice(true);
                if (!port) {
                    toast.error("No RD Service found on any port. Please check device.");
                    setConsent(false);
                    setIsDeviceConnected(false);
                    return;
                }

                infoUrl = getInfoUrl(port);
                status = await checkDeviceConnected(infoUrl);
                setIsDeviceConnected(status);

                if (!status) {
                    toast.error("Device not connected. Please check USB connection and try again.");
                    setConsent(false);
                    setIsDeviceConnected(false);
                    return;
                }
            }

            // 3. CAPTURE — only once
            const captureUrl = getCaptureUrl(port);
            const xmlData =
                originalConfigRef.current.xmlData ||
                deviceConfig?.xmlData ||
                `<PidOptions ver="1.0"><Opts fCount="1" fType="2" iCount="0" iType="0" pCount="0" format="0" pidVer="2.0" timeout="10000" posh="UNKNOWN" env="P" wadh=""/></PidOptions>`;

            const response = await axios({
                method: 'CAPTURE',
                url: captureUrl,
                headers: { "Content-Type": "text/xml; charset=UTF-8" },
                data: xmlData,
                timeout: 30000,
            });

            const jsonData = xmlJs.xml2json(response.data, { compact: true, spaces: 4 });
            const XMLjson = JSON.parse(jsonData);

            console.log("Capture response:", response);

            if (XMLjson?.PidData?.Resp?._attributes?.errCode === "0") {
                setFingerprint(response.data);
            } else {
                const errCode = XMLjson?.PidData?.Resp?._attributes?.errCode;
                const errInfo = XMLjson?.PidData?.Resp?._attributes?.errInfo;
                toast.error(`Capture failed (${errCode || 'Unknown'}): ${errInfo || 'Please try again'}`);
                setFingerprint(null);

                // Clear cache so next attempt rediscovers
                clearRDCache();
                setActivePort(null);
            }
        } catch (error) {
            console.error("Capture error:", error);
            toast.error("Capture failed. Will rediscover port on next attempt.");
            clearRDCache();
            setActivePort(null);
            setIsDeviceConnected(false);
        } finally {
            isCapturingRef.current = false;
        }
    };

    useEffect(() => {
        ekyChackApproved();
    }, [subTabs]); // eslint-disable-line react-hooks/exhaustive-deps

    const subChangeTab = (activeKey) => {
        setSubTabs(activeKey);
    };

    const handleCloseModal = () => {
        setAuthAePSModel(false);
    };

    const items = [
        {
            key: '1',
            label: 'Cash Withdrawal',
            children: (
                <CashWithdrawal
                    deviceConfig={deviceConfig}
                    getBankList={getBankList}
                    subChangeTab={subChangeTab}
                    existInputData={existInputData}
                    existData={getDataField}
                    clearExistInputData={clearExistInputData}
                />
            ),
        },
        {
            key: '2',
            label: 'Balance Enquiry',
            children: (
                <BalanceEnquiry
                    deviceConfig={deviceConfig}
                    getBankList={getBankList}
                    subChangeTab={subChangeTab}
                    existInputData={existInputData}
                    existData={getDataField}
                    clearExistInputData={clearExistInputData}
                />
            ),
        },
        {
            key: '3',
            label: 'Mini Statement',
            children: (
                <MiniStatement
                    deviceConfig={deviceConfig}
                    getBankList={getBankList}
                    subChangeTab={subChangeTab}
                    existInputData={existInputData}
                    existData={getDataField}
                    clearExistInputData={clearExistInputData}
                />
            ),
        },
        {
            key: '4',
            label: 'AePS History',
            children: <AePSHistory />,
        },
    ];

    // ---------- Consent checkbox ----------
    const onChangeChackBox = async (e) => {
        const newChecked = e.target.checked;
        if (newChecked) {
            let port = activePort;

            if (!port) {
                port = await discoverDevice(false);
            }

            let infoUrl = getInfoUrl(port);
            let status = await checkDeviceConnected(infoUrl);

            // If failed → force rescan other ports (same scheme)
            if (!status) {
                clearRDCache();
                setActivePort(null);
                port = await discoverDevice(true);
                infoUrl = getInfoUrl(port);
                status = await checkDeviceConnected(infoUrl);
            }

            setIsDeviceConnected(status);
            if (!status) {
                toast.error("Fingerprint Scanner device not connected, Check connection and try again");
                return;
            }
        }
        setConsent(newChecked);
    };

    return (
        <div>
            <div className="content-wrapper pt-2">
                <div className="content">
                    {contextHolder}
                    <div className="container-fluid pt-2 pb-5">
                        <div className="row">
                            <div className="col-md-12">
                                <div className='card rounded-0 card-body'>
                                    <Tabs defaultActiveKey="1" tabPosition={tabPosition}>
                                        <TabPane tab={"Transact"} key={1}>
                                            <div className="col-12 text-right">
                                                <strong className="text-danger fa-xl me-3">
                                                    Powered By NSDL
                                                </strong>

                                                <button
                                                    className="btn btn-primary rounded-pill pl-4 pr-4 ml-3"
                                                    onClick={() => setAuthAePSModel(true)}
                                                >
                                                    Re-login
                                                </button>
                                            </div>

                                            <Tabs
                                                defaultActiveKey={'1'}
                                                activeKey={subTabs}
                                                tabPosition={'top'}
                                                items={items}
                                                type="card"
                                                onChange={subChangeTab}
                                            />
                                        </TabPane>
                                        <TabPane tab={"Settel To Bank"} key={2}>
                                            <SettelTOBank />
                                        </TabPane>
                                        <TabPane tab={"Device Setting"} key={3}>
                                            {/* empty */}
                                        </TabPane>
                                        <TabPane tab={"Bank Setting"} key={4}>
                                            <BankSetting />
                                        </TabPane>
                                        <TabPane tab={"Biometric Devices"} key={5}>
                                            <BioDeviceList />
                                        </TabPane>
                                        <TabPane tab={"AePS Register"} key={6}>
                                            <AePSRegister />
                                        </TabPane>
                                    </Tabs>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ====================== LOGIN MODAL ====================== */}
                {isAuthAePSModel && (
                    <Modal
                        title={"AePS Verification Secure Login"}
                        width={600}
                        open={isAuthAePSModel}
                        onCancel={() => handleCloseModal()}
                        footer={false}
                    >
                        {isErrorAuth ? (
                            <div>
                                <p className='text-danger'>
                                    {errorMessage &&
                                        errorMessage.replace(
                                            'please try again later',
                                            'Please wait until Bank approval'
                                        )}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className='text-center h-100'>
                                    <img
                                        src={'./../fingerprint-svgrepo-com.svg'}
                                        alt=''
                                        width={'175px'}
                                    />
                                </div>
                                <div className='col-sm-12 col-md-12 col-lg-12 mb-120'>
                                    <div className='form-group'>
                                        <Checkbox
                                            className='d-flex'
                                            checked={isConsent}
                                            onChange={onChangeChackBox}
                                        >
                                            I hereby give my voluntary consent to SR PAY Private
                                            Limited, use my Aadhaar/ VID number & biometric to
                                            authenticate from UIDAI for the purpose of AEPS
                                            transaction.
                                        </Checkbox>
                                    </div>
                                </div>
                                <BioDevice />
                                <button
                                    className="btn btn-primary btn-lg bg-primary btn-block"
                                    onClick={scanFingerPrintData}
                                    disabled={!isConsent || !isDeviceConnected || isDiscovering}
                                >
                                    {isDiscovering ? "Searching Device..." : "Scan Finger"}
                                </button>
                            </>
                        )}

                        {/* Device Status */}
                        <div className="text-center mb-2 mt-3">
                            {isDiscovering ? (
                                <span className="text-primary fw-bold">
                                    🔍 Searching for biometric device...
                                </span>
                            ) : isDeviceConnected ? (
                                <span className="text-success fw-bold">
                                    🟢 Fingerprint Device Connected
                                   
                                </span>
                            ) : (
                                <span className="text-danger fw-bold">
                                    🔴 Fingerprint Device Not Connected
                                </span>
                            )}
                        </div>
                    </Modal>
                )}
            </div>
        </div>
    );
};

export default MyComponent;